import { vendorHeadlinePrice } from "./schemas/vendor-checkout";
import type { ListingPricing, VendorDeliveryFeeTier } from "./schemas/vendor-checkout";
import { toEventStartLocal } from "@libs/link-in-bio-config";
import {
  linkPageEventAccessSchema,
  type LinkPageEventAccess,
} from "./schemas/link-page-access";
import type { AccessLineupRow } from "./schemas/access-lineup";
import { eventDisplayTitle } from "./event-title";
import { adValueFromMinor } from "./ad-tracking";

export type LinkPageEventTier = {
  engineItemId: string;
  name: string;
  priceMinor: number;
  currency: string;
  saleStartsAt: string | Date | null;
  saleEndsAt: string | Date | null;
  /** Waiting on another tier to run out, no time known (LIS-912); absent is
   *  false. `saleStartsAt` already carries any opening time. */
  saleAwaitingTrigger?: boolean;
  gated: boolean;
  active: boolean;
  soldOut: boolean;
};

/** Everything the fan page needs, already loaded. Pure: no I/O, no clock. */
export type LinkPageEventInput = {
  artist: {
    slug: string;
    id: string;
    name: string;
    imageUrl: string | null;
    /** Venues, promoters and agencies host events; they are not an act. */
    partner?: boolean;
  };
  lineup?: AccessLineupRow[];
  access: {
    id: string;
    title: string;
    kind?: LinkPageEventAccess["access"]["kind"];
    engineEventId: string | null;
    description: string | null;
    instructions?: string | null;
    startDateTime: string | Date;
    startTimeKnown: boolean;
    timezone: string | null;
    endDateTime: string | Date | null;
    /** Set once the event is cancelled: the page says so and sells nothing. */
    cancelledAt?: string | Date | null;
    city: string | null;
    venue: string | null;
    /** Optional full street address, shown under the venue line. */
    address?: string | null;
    /** Show Location above the Line-up; default keeps the Line-up first. */
    locationFirst?: boolean;
    country: string | null;
    /** Already CDN-resolved. */
    imageUrl: string | null;
    /** First external ticket link, if the access sells elsewhere. */
    externalTicketUrl: string | null;
    /** The page asks for an email before the external ticket link. */
    collectEmail?: boolean;
    /** Legacy (non-engine) price; ignored when engine tiers exist. */
    price: number | null;
    priceCurrency: string | null;
    /** Legacy (non-engine) remaining tickets; engine events report null. */
    availableTicketCount: number | null;
    /** Engine events' per-order ticket limits across all their tiers
     *  (LIS-924), as the ticketing projection mirrors them; null: none. */
    minTicketsPerOrder?: number | null;
    maxTicketsPerOrder?: number | null;
  };
  engineTiers?: LinkPageEventTier[];
  /** The 1-tap listing the page buys from, by its prices (the server's
   *  journey): its headline price is the checkout's. */
  listing?: (ListingPricing & { deliveryFeeTiers?: readonly VendorDeliveryFeeTier[] }) | null;
};

const toIso = (value: string | Date | null) =>
  value ? new Date(value).toISOString() : null;

/** Turn stored rows into the wire shape the Link-in-Bio event page renders.
 *  Shared by platform-api (from the database) and the editors' live preview
 *  (from an unsaved draft) so both apply the same rules. */
export function resolveLinkPageEventAccess(
  input: LinkPageEventInput,
): LinkPageEventAccess {
  const { access, artist } = input;
  // A partner organiser is not an act, so it leaves the fan-facing line-up.
  // When it was the only row there is no line-up to show at all: the wire
  // schema wants at least one act or none, never an empty list.
  const filtered = artist.partner
    ? input.lineup
        ?.filter((row) => row.type !== "artist" || row.artistId !== artist.id)
        .map((row, index) => ({ ...row, position: index + 1 }))
    : input.lineup;
  const lineup = filtered?.length ? filtered : undefined;
  const tiers = access.engineEventId ? (input.engineTiers ?? []) : undefined;
  const activeTiers = tiers?.filter((t) => t.active) ?? [];
  // Engine events price from their cheapest live tier; the legacy price column
  // is not maintained for them. Legacy events keep the old rules.
  const priceCurrency = tiers
    ? (activeTiers[0]?.currency ?? access.priceCurrency ?? "GBP")
    : (access.priceCurrency ?? "GBP");
  const priceAmount = tiers
    ? activeTiers.length
      // The engine still uses Intl's decimals. Vendor prices below use ISO.
      ? adValueFromMinor(
          Math.min(...activeTiers.map((t) => t.priceMinor)),
          priceCurrency,
        )
      : null
    : access.price;
  // More than one ticket price: the row reads "From <lowest>".
  const priceFrom = new Set(activeTiers.map((t) => t.priceMinor)).size > 1;
  // A vendor event's stored price leaves out the vendor's per-order fee, so
  // the page would say less than the checkout charges: its catalog prices it.
  const vendorPrice = input.listing ? vendorHeadlinePrice(input.listing) : null;
  const headlineAmount = vendorPrice?.amount ?? priceAmount;
  const cancelledAt = toIso(access.cancelledAt ?? null);
  const checkoutMode = cancelledAt
    ? "unavailable"
    : access.externalTicketUrl !== null
      ? "external"
      : headlineAmount === null
        ? "unavailable"
        : headlineAmount === 0
          ? "free"
          : "paid";
  return linkPageEventAccessSchema.parse({
    slug: artist.slug,
    artist: { id: artist.id, name: artist.name, imageUrl: artist.imageUrl },
    access: {
      type: "event",
      lineup,
      engineEventId: access.engineEventId,
      engineTiers: tiers?.map((t) => ({
        ...t,
        saleStartsAt: toIso(t.saleStartsAt),
        saleEndsAt: toIso(t.saleEndsAt),
      })),
      // Only an engine event's own: the tickets sheet bounds its steppers
      // by them before the checkout opens.
      ...(tiers
        ? {
            minTicketsPerOrder: access.minTicketsPerOrder ?? null,
            maxTicketsPerOrder: access.maxTicketsPerOrder ?? null,
          }
        : {}),
      id: access.id,
      title: eventDisplayTitle({
        title: access.title,
        venue: access.venue,
        artistName: artist.name,
      }),
      kind: access.kind ?? null,
      description: access.description,
      instructions: access.instructions ?? null,
      startsAt: toIso(access.startDateTime),
      startsAtLocal: toEventStartLocal(
        access.startDateTime,
        access.timezone,
        access.startTimeKnown,
      ),
      endsAt: toIso(access.endDateTime),
      ...(cancelledAt ? { cancelledAt } : {}),
      city: access.city ?? "",
      venue: access.venue ?? "",
      address: access.address ?? null,
      locationFirst: access.locationFirst ?? false,
      country: access.country ?? "",
      imageUrl: access.imageUrl ?? artist.imageUrl,
      externalTicketUrl: access.externalTicketUrl,
      ...(access.externalTicketUrl !== null && access.collectEmail
        ? { collectEmail: true }
        : {}),
      checkoutMode,
      price:
        vendorPrice ??
        (priceAmount === null
          ? null
          : { amount: priceAmount, currency: priceCurrency, from: priceFrom }),
      availableTicketCount: access.availableTicketCount,
    },
    // The read's purchase is compiled by the server for its reader and
    // set on the page it sends (LIS-1173): none from stored rows alone.
    purchase: null,
  });
}
